import { Input, InputGroup, InputGroupLabel } from '@sbab-ui/components'
import React, { ChangeEvent, useRef } from 'react'
import { AppAction } from '../../appState.ts'
import useForm from '../../hooks/useForm.ts'
import FormWrapper from './FormWrapper.tsx'

interface Props {
  id: string
  value: string
  appDispatch: React.Dispatch<AppAction>
  label: string
  description?: string
  suffix?: string
  validationFunction: (value: string) => string
  formatFunction?: (value: string) => string
  maxLength?: number
  step?: number
  placeholder?: string
  isFetching?: boolean
  isValidatingPostalCode?: boolean
  validateWithFetch?: () => void
}

export default function FormWithInput({
  id,
  value,
  appDispatch,
  label,
  description,
  suffix,
  validationFunction,
  formatFunction = (value) => (value ? new Intl.NumberFormat('sv-SE').format(Number(value)) : ''),
  maxLength = 10,
  step,
  placeholder = '',
  isFetching = false,
  isValidatingPostalCode = false,
}: Readonly<Props>) {
  const { inputRef, formattedValue, errorMessage, hasBeenTouched, onChangeHandler, submitHandler } =
    useForm({
      initialValue: value,
      appDispatch,
      id,
      validationFunction,
      formatFunction,
      maxLength,
    })

  const submitButtonRef = useRef<HTMLButtonElement>(null)
  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      submitButtonRef.current?.click()
    }
  }

  const disabled = !!errorMessage || !hasBeenTouched
  const loading = isFetching || isValidatingPostalCode

  return (
    <FormWrapper
      id={id}
      appDispatch={appDispatch}
      step={step}
      label={label}
      description={description}
      errorMessage={errorMessage}
      inputRef={inputRef}
      submitButtonRef={submitButtonRef}
      submitHandler={submitHandler}
      disabled={disabled}
      loading={loading}
    >
      <InputGroup block className="!w-full sm:!max-w-[375px]">
        <Input
          value={formattedValue}
          onChange={onChangeHandler as (event: ChangeEvent<HTMLInputElement>) => void}
          onBlur={onChangeHandler as (event: ChangeEvent<HTMLInputElement>) => void}
          onKeyPress={handleKeyPress}
          id={id}
          data-label={id}
          aria-label={label}
          invalid={!!errorMessage}
          ref={inputRef}
          placeholder={placeholder}
          className="!w-full"
          inputMode="numeric"
        />
        {suffix && <InputGroupLabel className="shrink-0">{suffix}</InputGroupLabel>}
      </InputGroup>
    </FormWrapper>
  )
}
